import React, { useState, useEffect, createContext, useCallback, useMemo } from 'react';
import './PacingAnalysis.css';
import { Box, Container, CircularProgress } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import _ from 'lodash';
import axios from 'axios';
import PacingPortfolioInformation from '../../components/PacingPortfolioInformation/PacingPortfolioInformation';
import PacingFilters from '../../components/PacingFilters/PacingFilters';
import PacingInteractiveGraph from '../../components/PacingInteractiveGraph/PacingInteractiveGraph';
import PacingInvestmentItems from '../../components/PacingInvestmentItems/PacingInvestmentItems';
import DownloadPacingExcelButton from '../../components/DownloadPacingExcelButton/DownloadPacingExcelButton';
import PageTitle from '../../components/PageTitle/PageTitle';
import PacingScenarios from '../../components/PacingScenarios/PacingScenarios';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

export const PacingContext = createContext();
const base_url = 'https://jasperd.ca';

const PacingAnalysis = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialData = location.state || {};

  const [portfolioInfo, setPortfolioInfo] = useState({
    portfolioBaseCurrency: initialData.portfolioInformation?.portfolioBaseCurrency || 'CAD',
    portfolioMarketValue: initialData.portfolioInformation?.portfolioMarketValue || 34500000,
    asOfDate: initialData.portfolioInformation?.asOfDate || '2024-03-31',
    growthRate: initialData.portfolioInformation?.growthRate || 0.05,
  });

  const [fundInformation, setFundInformation] = useState(initialData.fundInformation || {});
  const [pacingDataState, setPacingDataState] = useState(initialData.pacingData || {});
  const historicData = initialData.historicData || {};
  const futureTransactions = initialData.futureTransactions || {};
  const [scenariosState, setScenariosState] = useState(initialData.scenarios || {});

  const [filters, setFilters] = useState({
    assetClass: 'Private Equity',
    strategy: 'All',
    futureFund: 'All',
    fund: 'All',
  });

  const [filteredData, setFilteredData] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingFinished, setLoadingFinished] = useState(false);
  const [recalcTriggered, setRecalcTriggered] = useState(false);

  const unifyData = useCallback(() => {
    const rows = [];
    Object.keys(pacingDataState).forEach((fName) => {
      const byDate = pacingDataState[fName];
      Object.keys(byDate).forEach((d) => {
        let row = rows.find((r) => r.fund === fName && r.date === d);
        if (!row) {
          row = {
            date: d,
            fund: fName,
            assetClass: fundInformation[fName]?.assetClass || 'N/A',
            strategy: fundInformation[fName]?.strategy || 'N/A',
            fundStatus: fundInformation[fName]?.fundStatus || 'N/A',
            manager: fundInformation[fName]?.manager || 'N/A',
            vintage: fundInformation[fName]?.vintage || 'N/A',
            localCurrency: fundInformation[fName]?.localCurrency || 'N/A',
            marketValueBase: byDate[d].marketValueBase || 0,
            marketValueLocal: byDate[d].marketValueLocal || 0,
            contributionsLocal: byDate[d].contributionsLocal || 0,
            distributionsLocal: byDate[d].distributionsLocal || 0,
            modelContributionsLocal: byDate[d].modelContributionsLocal || 0,
            modelDistributionsLocal: byDate[d].modelDistributionsLocal || 0,
          };
          rows.push(row);
        }
      });
    });
    return rows;
  }, [pacingDataState, fundInformation]);

  const applyFilters = useCallback(
    (rows) => {
      let result = [...rows];
      if (filters.assetClass !== 'All') {
        result = result.filter((r) => r.assetClass === filters.assetClass);
      }
      if (filters.strategy !== 'All') {
        result = result.filter((r) => r.strategy === filters.strategy);
      }
      if (filters.futureFund !== 'All') {
        result = result.filter((r) => r.status === filters.futureFund);
      }
      if (filters.fund !== 'All') {
        result = result.filter((r) => r.fund === filters.fund);
      }
      return _.sortBy(result, (r) => r.date);
    },
    [filters]
  );

  const groupByFund = useCallback((rows) => _.groupBy(rows, 'fund'), []);

  useEffect(() => {
    const allRows = unifyData();
    const filteredRows = applyFilters(allRows);
    setFilteredData(groupByFund(filteredRows));
  }, [unifyData, applyFilters, groupByFund]);

  const rowArrayForFilters = useMemo(
    () => Object.values(filteredData).flat(),
    [filteredData]
  );

  const filterOptions = useMemo(() => ({
    assetClasses: _.uniq(rowArrayForFilters.map((d) => d.assetClass)),
    strategies: _.uniq(rowArrayForFilters.map((d) => d.strategy)),
    futureFunds: _.uniq(rowArrayForFilters.map((d) => d.status)),
    funds: _.uniq(rowArrayForFilters.map((d) => d.fund)),
  }), [rowArrayForFilters]);

  const handlePortfolioInfoChange = useCallback((field, value) => {
    setPortfolioInfo((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handlePacingDataUpdate = useCallback((fundName, updatedPacing) => {
    setPacingDataState((prev) => ({ ...prev, [fundName]: updatedPacing }));
  }, []);

  const handleFundInfoUpdate = useCallback((fundName, newFundInfo) => {
    setFundInformation((prev) => ({
      ...prev,
      [fundName]: { ...prev[fundName], ...newFundInfo },
    }));
  }, []);

  const handleScenarioChange = useCallback((field, value) => {
    setScenariosState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const combinedPacingData = {
    portfolioInformation: portfolioInfo,
    fundInformation,
    historicData,
    futureTransactions,
    pacingData: pacingDataState,
    scenarios: scenariosState,
  };

  const pollTaskStatus = (taskId) => {
    const interval = setInterval(() => {
      axios.get(`${base_url}/api/task-status/${taskId}`, { validateStatus: () => true })
        .then((response) => {
          const result = response.data;
          if (result.status === 'completed') {
            clearInterval(interval);
            try {
              const newData = result?.result || {};
              setLoading(false);
              setPortfolioInfo(newData.portfolioInformation);
              setFundInformation(newData.fundInformation);
              setPacingDataState(newData.pacingData);
              setScenariosState(newData.scenarios);
            } catch (error) {
              console.error('Error processing backend data:', error);
              setLoading(false);
            }
          } else if (result.status === 'error') {
            console.error('Task failed:', result.error);
            setLoading(false);
            clearInterval(interval);
          }
        })
        .catch((error) => {
          console.error('Error fetching task status:', error);
          setLoading(false);
          clearInterval(interval);
        });
    }, 3000);
  };

  const handleRecalculate = async () => {
    setRecalcTriggered(true);
    setLoading(true);
    try {
      const response = await axios.post(
        `${base_url}/api/submit-pacing-data-dict`,
        combinedPacingData,
        { validateStatus: () => true }
      );
      pollTaskStatus(response.data.task_id);
    } catch (error) {
      console.error('Error submitting pacing data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading && recalcTriggered) {
      setLoadingFinished(true);
      setRecalcTriggered(false);
      const timer = setTimeout(() => setLoadingFinished(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [loading, recalcTriggered]);

  const fundNames = Object.keys(filteredData);

  return (
    <PacingContext.Provider value={{ filteredData, setFilteredData }}>
      <div className={`pacing-screen ${loading ? 'loading' : ''}`}>
        <Container className="pacing-screen-container">
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <PageTitle title="Private Investment Pacing Model" subtitle="Analysis" />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, my: 3 }}>
            <DownloadPacingExcelButton pacingData={combinedPacingData} />
          </Box>
          {fundNames.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <PacingPortfolioInformation
                portfolioInfo={portfolioInfo}
                handlePortfolioInfoChange={handlePortfolioInfoChange}
              />
              <PacingScenarios
                scenarios={scenariosState}
                onScenarioChange={handleScenarioChange}
                onRecalculate={handleRecalculate}
              />
              <PacingFilters filters={filters} setFilters={setFilters} {...filterOptions} />
              <PacingInteractiveGraph
                fundDataMap={filteredData}
                fundInformation={fundInformation}
                pacingData={pacingDataState}
                portfolioInfo={portfolioInfo}
                asOfDate={portfolioInfo.asOfDate}
              />
              <PacingInvestmentItems
                fundDataMap={filteredData}
                portfolioInformation={portfolioInfo}
                fundInformation={fundInformation}
                historicData={historicData}
                futureCashflows={futureTransactions}
                pacingData={pacingDataState}
                scenarios={scenariosState}
                onFundInfoUpdate={handleFundInfoUpdate}
                onPacingDataUpdate={handlePacingDataUpdate}
              />
            </>
          )}
        </Container>
        {loading && (
          <div className="loading-indicator">
            <CircularProgress color="inherit" />
          </div>
        )}
        {loadingFinished && (
          <div className="loading-finished">
            <CheckCircleOutlineIcon style={{ fontSize: '3rem' }} />
            <span>Load Complete!</span>
          </div>
        )}
      </div>
    </PacingContext.Provider>
  );
};

export default PacingAnalysis;